/**
 * AddToHomeScreenPrompt — one-time PWA install prompt
 *
 * Surfaces 30 s after login on mobile, exactly once per user-decision cycle.
 * Two device-specific paths:
 *
 *   1) Android / Chromium-based browsers
 *      The browser fires a `beforeinstallprompt` event when the page meets
 *      Chrome's installability heuristics (manifest + SW registered + some
 *      user engagement). We `preventDefault()` it to suppress Chrome's mini-
 *      infobar, stash the event, and replay it via `prompt()` on the user's
 *      explicit click of our "Install" button. The browser then shows its
 *      native install dialog. We then read `userChoice` to know if they
 *      accepted, dismissed, or hit the X.
 *
 *   2) iOS Safari
 *      iOS does NOT fire `beforeinstallprompt` and offers no JS API for
 *      programmatic installation. The only path is for the user to tap
 *      Share → Add to Home Screen manually. So our UI on iOS is a small
 *      instruction sheet that walks them through the gesture.
 *
 * Suppression / re-show rules:
 *   - Already installed (display-mode: standalone or navigator.standalone) → never show
 *   - Already accepted → never show again (permanent)
 *   - User dismissed → suppress for 7 days, then surface once more
 *   - Not authenticated → never show
 *   - Not on a mobile viewport → never show
 *   - Standard 30 s post-mount delay before first surface
 *
 * Safety: this component renders nothing if any precondition fails, so
 * desktop logged-out / desktop logged-in / installed-PWA users never see it.
 */

import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShareNetwork, House, ArrowDown, DownloadSimple } from '@phosphor-icons/react';
import { isAuthenticated } from '@/utils/auth';

const STORAGE_KEY = 'paygbot:a2hs:state';
const DEFER_DAYS = 7;
const DEFER_MS = DEFER_DAYS * 24 * 60 * 60 * 1000;
const FIRST_SHOW_DELAY_MS = 30_000;

// --- Device detection helpers (run client-side only) -----------------------

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari
  if (window.navigator?.standalone === true) return true;
  // Android / Chromium / desktop installed PWA
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  if (window.matchMedia?.('(display-mode: minimal-ui)')?.matches) return true;
  return false;
};

const isIOS = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  // iPhone, iPad, iPod. iPadOS 13+ reports as Mac on touch — handle that too.
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true;
  if (/Macintosh/.test(ua) && 'ontouchend' in document) return true;
  return false;
};

const isMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  // Any touch device with viewport <= 1024 — covers phones AND tablet portrait.
  return window.matchMedia?.('(max-width: 1024px)')?.matches;
};

// Read / write the user's prior decision so we don't pester them.
const readState = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const writeState = (state) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable (private mode / quota) — fail open. */
  }
};

const shouldSuppress = () => {
  const state = readState();
  if (!state) return false;
  if (state.outcome === 'accepted') return true;       // installed → never show again
  if (state.outcome === 'dismissed') {
    const since = Date.now() - (state.timestamp || 0);
    if (since < DEFER_MS) return true;                 // within the 7-day cool-down
  }
  return false;
};

export default function AddToHomeScreenPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);  // beforeinstallprompt event
  const [platform, setPlatform] = useState(null);  // 'android' | 'ios' | null

  // ------------------------------------------------------------------ mount
  useEffect(() => {
    // Hard preconditions — if ANY fail, this component is a no-op.
    if (typeof window === 'undefined') return undefined;
    if (!isAuthenticated()) return undefined;
    if (isStandalone()) return undefined;             // already installed
    if (!isMobileViewport()) return undefined;         // desktop — skip
    if (shouldSuppress()) return undefined;            // user previously decided

    let cancelled = false;
    let bipHandler = null;
    let installedHandler = null;

    // Android / Chromium path.
    bipHandler = (e) => {
      e.preventDefault();
      if (cancelled) return;
      setDeferred(e);
      setPlatform('android');
    };
    window.addEventListener('beforeinstallprompt', bipHandler);

    // If the OS reports a successful install (e.g. via Chrome menu, not our
    // button), record it so we never prompt again.
    installedHandler = () => {
      writeState({ outcome: 'accepted', timestamp: Date.now() });
      if (!cancelled) setVisible(false);
    };
    window.addEventListener('appinstalled', installedHandler);

    // iOS path — there's no event; we trigger the prompt unconditionally
    // (after the delay) for iOS users who pass the precondition checks.
    if (isIOS()) {
      setPlatform((prev) => prev || 'ios');
    }

    const showTimer = window.setTimeout(() => {
      if (cancelled) return;
      // Show only if we have something useful to show:
      //   - Android: only if beforeinstallprompt has fired (we have an event)
      //   - iOS: always (no event needed, instructions are sufficient)
      if (isIOS() || deferred) {
        setVisible(true);
      } else {
        // Android browser hasn't deemed us installable yet (engagement
        // heuristics not met). The bipHandler will set `deferred` whenever
        // it fires, which schedules the prompt below via the second effect.
      }
    }, FIRST_SHOW_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(showTimer);
      if (bipHandler) window.removeEventListener('beforeinstallprompt', bipHandler);
      if (installedHandler) window.removeEventListener('appinstalled', installedHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the beforeinstallprompt event arrives AFTER the 30 s delay, surface
  // the prompt now. (User passed the engagement threshold a bit late.)
  useEffect(() => {
    if (!deferred) return;
    if (visible) return;
    if (shouldSuppress()) return;
    setVisible(true);
  }, [deferred, visible]);

  const handleDismiss = useCallback(() => {
    writeState({ outcome: 'dismissed', timestamp: Date.now() });
    setVisible(false);
  }, []);

  const handleInstallAndroid = useCallback(async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === 'accepted') {
        writeState({ outcome: 'accepted', timestamp: Date.now() });
      } else {
        writeState({ outcome: 'dismissed', timestamp: Date.now() });
      }
    } catch {
      // The browser may throw if prompt() is called more than once or the
      // event is stale. Treat as "user moved on".
      writeState({ outcome: 'dismissed', timestamp: Date.now() });
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  }, [deferred]);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="a2hs-prompt"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 24, stiffness: 240 }}
        className="fixed left-3 right-3 z-[80] bg-slate-900 border border-blue-500/40 rounded-2xl shadow-2xl"
        style={{
          // Sit above iPhone home-indicator + iOS Safari URL bar.
          bottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
          maxWidth: 'calc(100vw - 1.5rem)',
        }}
        data-testid="a2hs-prompt"
        role="dialog"
        aria-labelledby="a2hs-title"
      >
        <div className="relative px-4 py-3">
          <button
            type="button"
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors p-1"
            aria-label="Dismiss"
            data-testid="a2hs-dismiss-btn"
          >
            <X size={16} />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
              <House size={20} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p
                id="a2hs-title"
                className="text-white text-sm font-semibold"
              >
                Install PayGBot on your phone
              </p>
              <p className="text-slate-400 text-xs mt-0.5">
                {platform === 'ios'
                  ? 'One-tap access from your home screen — no app store.'
                  : 'Get one-tap access and a fullscreen, app-like experience.'}
              </p>
            </div>
          </div>

          {/* Platform-specific call-to-action */}
          {platform === 'android' && deferred && (
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleInstallAndroid}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
                data-testid="a2hs-install-btn"
              >
                <DownloadSimple size={16} weight="bold" />
                Install
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="px-3 py-2 text-slate-400 hover:text-white text-sm transition-colors"
                data-testid="a2hs-later-btn"
              >
                Maybe later
              </button>
            </div>
          )}

          {platform === 'ios' && (
            <div className="mt-3 space-y-2">
              <ol className="space-y-1.5 text-xs text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    1
                  </span>
                  <span className="flex items-center gap-1.5">
                    Tap the
                    <ShareNetwork size={14} className="text-blue-300" weight="bold" />
                    Share icon
                    <ArrowDown size={12} className="text-slate-500 ml-0.5" />
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    2
                  </span>
                  <span>Choose <span className="text-white font-medium">Add to Home Screen</span></span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    3
                  </span>
                  <span>Tap <span className="text-white font-medium">Add</span> in the top-right</span>
                </li>
              </ol>
              <button
                type="button"
                onClick={handleDismiss}
                className="w-full mt-1 px-3 py-1.5 text-slate-400 hover:text-white text-xs transition-colors"
                data-testid="a2hs-got-it-btn"
              >
                Got it
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
